// The SIP messages a trace produced, for one line.
//
// Kevin, 2026-08-22: "what do you do after a trace has been run on a line?" This is the answer.
// Before it, a trace could be started and stopped from the Diagnostics card and the evidence went
// nowhere — the worker was already fetching `sip_history` into the live-quality response and the UI
// dropped it on the floor.
//
// WHY A DIALOG AND NOT THE DIAGNOSTICS RAIL. These rows are ~100 characters of monospace each and
// the rail is a narrow column; wrapped SIP is unreadable, and a truncated `INVITE sip:…` hides the
// part that matters. So the card carries the findings — the sentences — and the table lives here.
//
// WHAT IS NOT HERE, deliberately:
//
//  - The raw ring. `pjsip show history` is host-wide: on PBX-01 it contains OPTIONS exchanges with
//    other customers' devices. The worker matches rows against this line's own scope before they
//    are serialised, so this component never holds another tenant's signalling. See lib/sipHistory.js.
//  - Audio. Nothing in the trace path captures media — `rtp set debug` logs headers only, and we do
//    not enable it at all. There is no recording to offer and no button implying one.
//  - Auth digests. The agent redacts unconditionally before the ring is served.
//
// `matched of total_in_ring` is stated at the top rather than just showing the count, because "4
// messages" reads as a quiet line when it actually means "4 of this line's, out of 900 on the host".
// A short list here is a claim about the filter, not about the phone.

import SipMessageTable, { CopyButton, traceAsText } from './SipMessageTable.jsx';

export default function LineSipHistoryDialog({ line, sip, extensions = [], onClose }) {
  const rows = sip?.rows || [];
  // Extensions the address filter pulled in that are not this line's.
  const others = sip?.other_extensions || [];
  const mine = extensions;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            SIP messages · {line.number || line.label || `line ${line.id}`}
          </h2>
          {/* THE CAPTION HAS TO MATCH WHAT THE FILTER DID. It used to promise "the rest belong to
              other lines and are not shown" — then a trace on one of Kevin's test lines returned
              all three phones behind his router, because the address half of the filter matches
              every device at that address. Saying so is the difference between a table an operator
              can trust and one that quietly includes somebody else. */}
          <p className="mt-1 text-sm text-gray-600">
            {sip?.matched ?? 0} message{(sip?.matched ?? 0) === 1 ? '' : 's'} for this line, from{' '}
            {sip?.total_in_ring ?? 0} in the host&apos;s buffer.
            {others.length > 0
              ? ` Includes traffic from ${others.length} other extension${others.length === 1 ? '' : 's'}`
                + ` behind the same address (${others.join(', ')}) — highlighted below.`
              : ' The rest belong to other lines on the same PBX and are not shown.'}
          </p>
        </div>

        {Boolean(sip?.findings?.length) && (
          <div className="space-y-1 border-b border-gray-200 bg-gray-50 px-5 py-3">
            {sip.findings.map((f) => (
              <p
                key={f.text}
                className={`text-sm leading-snug ${
                  f.level === 'warn' ? 'text-amber-800' : 'text-gray-600'}`}
              >
                {f.text}
              </p>
            ))}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto px-5 py-3">
          {rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              Nothing for this line in the buffer yet. A trace only records while it is running —
              place or receive a call on this number, then reopen this.
            </p>
          ) : (
            <SipMessageTable rows={rows} mine={mine} />
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-3">
          {/* Copy is on the LEFT, away from Close: the trace is the thing worth taking with you,
              and putting it next to the dismiss button invites losing it by a pixel. */}
          <CopyButton
            disabled={!sip || !sip.matched}
            text={sip ? traceAsText({ line, sip, mine }) : ''}
          />
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
